import type {
    AlertsProps,
    CompliancesProps,
    FundingHistoryProps,
    IdentityProps,
    LegalCase,
    LegalGroup,
    LegalRiskProps,
    ManagementProps,
    McaDocsProps,
    MonthCell,
    OverviewProps,
    RelatedPartiesProps,
    RelatedPartyEntity,
    ReportData,
    ScoreProps,
    ShareholdingProps,
} from './types';
import type { Severity } from './designTokens';
import {
    normalizeAuditedStatements,
    normalizeBankingOverview,
    normalizeDirectorBureau,
    normalizeDirectorsDeep,
    normalizeFinancialMetrics,
    normalizeInsights,
    normalizeMcaCharges,
    normalizeProvisionalStatements,
    normalizeGstOverview,
    normalizeGstRevenue,
    normalizeGstExpenses,
    normalizeAggBanking,
} from './normalizersExt';
import { truncateText } from './overviewHelpers';

export {
    normalizeAuditedStatements,
    normalizeBankingOverview,
    normalizeDirectorBureau,
    normalizeDirectorsDeep,
    normalizeInsights,
    normalizeMcaCharges,
    normalizeProvisionalStatements,
    normalizeInsightsBlock,
} from './normalizersExt';

const MONTH_SHORT: Record<string, string> = {
    Jan: 'Jan',
    Feb: 'Feb',
    Mar: 'Mar',
    Apr: 'Apr',
    May: 'May',
    Jun: 'Jun',
    Jul: 'Jul',
    Aug: 'Aug',
    Sep: 'Sep',
    Oct: 'Oct',
    Nov: 'Nov',
    Dec: 'Dec',
};

export function normalizeIdentity(basic: any, mainOverview: any, unifiedOverview: any, orgInfo?: any): IdentityProps {
    const p = basic?.portfolioCompanyDetails ?? {};
    const d = mainOverview?.data ?? {};
    const po = unifiedOverview?.responseBody?.data ?? {};
    const oi = orgInfo?.orgInfo ?? {};
    const oiCf = oi.customFields ?? {};
    const subjectCompetitor = Array.isArray(po.competitors)
        ? po.competitors.find((c: any) => /subject\s+company/i.test(c?.description ?? ''))
        : null;
    const gstins: IdentityProps['gstins'] = Array.isArray(d.gstin)
        ? d.gstin.flatMap((entry: any) => Object.entries(entry ?? {}).map(([state, gstin]) => ({ state, gstin: String(gstin) })))
        : [];
    const foundationRaw: string | undefined = d.foundationDate ?? oiCf.foundationDate ?? p.customFields?.foundationDate;
    const foundationDate = foundationRaw?.includes('T') ? foundationRaw.slice(0, 10) : foundationRaw;

    const wt = d.websiteTraffic ?? po.websiteTraffic;
    const websiteTraffic =
        wt && Array.isArray(wt.momWebsiteTraffic) && wt.momWebsiteTraffic.length
            ? {
                  change: wt.change,
                  trendText: wt.trendText,
                  period: wt.period,
                  monthly: wt.momWebsiteTraffic.map((m: any) => ({
                      yearMonth: String(m.yearMonth ?? ''),
                      traffic: Number(m.traffic ?? 0),
                  })),
              }
            : undefined;

    return {
        orgName: oi.orgDisplayName ?? oi.orgName ?? p.orgName ?? d.orgName ?? subjectCompetitor?.companyName ?? '—',
        pan: oi.identifier ?? p.pan ?? d.panNumber ?? '—',
        cin: oi.cinNumber ?? d.cinNumber ?? p.cin ?? '—',
        sector: oiCf.sector ?? p.sector ?? p.customFields?.sector ?? po?.sectorDetails?.sector ?? '—',
        subSector: po?.sectorDetails?.subSector,
        foundationDate,
        state: oiCf.state ?? p.customFields?.state ?? d.headquarterLocation?.state,
        fullAddress: oiCf.fullAddress ?? d.headquarterLocation?.fullAddress ?? p.customFields?.fullAddress,
        websiteUrl: d.socialInfo?.websiteUrl ?? p.websiteUrl,
        aicaScore: p.aicaScore,
        companyAccessType: oi.accountType ?? p.companyAccessType,
        lastFetched: p.lastFetched,
        gstConnected: p.gstConnected,
        contactEmail: p.contactEmail,
        gstins,
        profileStatus: p.profileGenerationStatus,
        startupSme: p.customFields?.startupSme ?? oiCf.startupSme,
        websiteTraffic,
    };
}

export function normalizeScore(raw: any): ScoreProps | undefined {
    if (!raw) return undefined;
    if (typeof raw.companyScore === 'number' && Array.isArray(raw.headers)) {
        return {
            companyScore: Number.isFinite(raw.companyScore) ? raw.companyScore : 0,
            headers: raw.headers.map((h: any) => ({
                displayName: h.displayName,
                tooltipDescription: h.tooltipDescription,
                headerScore: Number.isFinite(h.headerScore) ? h.headerScore : null,
                metrics: (h.metrics ?? []).map((m: any) => ({
                    displayName: m.displayName,
                    metricScore: Number.isFinite(m.metricScore) ? m.metricScore : null,
                    tooltipDescription: m.tooltipDescription,
                })),
            })),
        };
    }
    const card = Array.isArray(raw.aicaScoreCard) ? raw.aicaScoreCard : null;
    if (!card || card.length === 0) return undefined;
    const unique = new Map<string, number>();
    card.forEach((pair: any) => {
        const key = pair?.first;
        const val = Number(pair?.second);
        if (!key || !Number.isFinite(val)) return;
        if (!unique.has(key)) unique.set(key, val);
    });
    if (unique.size === 0) return undefined;
    const entries = Array.from(unique.entries());
    const companyScore = entries.reduce((s, [, v]) => s + v, 0) / entries.length / 10;
    return {
        companyScore,
        headers: entries.map(([displayName, score]) => ({
            displayName,
            headerScore: score / 10,
            metrics: [{ displayName, metricScore: score / 10 }],
        })),
    };
}

const PLACEHOLDER_RE =
    /^(\s*(n\/?a|na|none|nil|nan|null|undefined|tbd|—|-+)\s*|\s*data\s+(not\s+(found|available)|unavailable)\s*|\s*not\s+available\s*|\s*no\s+data\s*(available)?\s*)$/i;
const STRIP_HTML = (s: string) =>
    s
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
const cleanText = (v: any): string | undefined => {
    if (v == null) return undefined;
    const s = STRIP_HTML(String(v));
    if (!s || PLACEHOLDER_RE.test(s)) return undefined;
    return s;
};
const cleanList = (v: any): string[] | undefined => {
    if (!Array.isArray(v)) return undefined;
    const out = v.map((x) => cleanText(x)).filter((x): x is string => !!x);
    return out.length ? out : undefined;
};

export function normalizeOverview(raw: any): OverviewProps | undefined {
    const d = raw?.responseBody?.data;
    if (!d) return undefined;
    const o = d.overview ?? {};
    const so = d.sectorOutlook ?? {};
    const sd = d.sectorDetails ?? {};
    const cr = d.creditRating;
    const creditRating =
        cr && (cr.ratingSummary || Array.isArray(cr.ratingData))
            ? {
                  ratingSummary: cr.ratingSummary,
                  ratingData: (cr.ratingData ?? []).map((r: any) => ({
                      date: r.date ?? 'N/A',
                      agency: r.agency ?? 'N/A',
                      ratingShortTerm: r.ratingShortTerm ?? 'Not rated',
                      ratingLongTerm: r.ratingLongTerm ?? 'Not rated',
                      outlook: r.outlook ?? '—',
                      sourceLink: r.sourceLink && r.sourceLink !== 'N/A' ? r.sourceLink : undefined,
                  })),
              }
            : undefined;
    const news: OverviewProps['news'] = [];
    (d.newsReport ?? []).forEach((n: any) => {
        (n.news ?? []).forEach((item: any) => {
            if (!item?.title) return;
            news.push({
                title: item.title,
                publisherName: item.publisherName ?? '',
                publishedDate: item.publishedDate ?? '',
                snippet: item.snippet ?? '',
                sentiment: item.newsSentiment ?? 'Neutral',
                redirectUrl: item.redirectUrl ?? undefined,
            });
        });
    });
    return {
        overviewContent: cleanText(o.overviewContent) ?? '',
        fundingRaised: cleanText(o.fundingRaised),
        keyInvestors: cleanList(o.keyInvestors),
        productOfferings: cleanText(o.productOfferings),
        customersAndSalesChannels: cleanText(o.customersAndSalesChannels),
        vendorsSourcingManufacturing: cleanText(o.vendorsSourcingManufacturing),
        geographiesSegmentation: cleanText(o.geographiesSegmentation),
        competitorsOrListedPeer: cleanText(o.competitorsOrListedPeer),
        sectorOverview: cleanText(so.sectorOverview),
        sectoralPerformance: cleanText(sd.sectoralPerformance),
        marketSize: cleanText(so.marketSize),
        industryCagr: cleanText(so.industryCagr),
        marketGrowth: cleanText(so.marketGrowth),
        sectorRisks: cleanText(so.sectorRisks),
        recentTrends: cleanList(so.recentTrends),
        growthDrivers: cleanList(so.growthDrivers),
        keyRisks: cleanList(so.keyRisks),
        creditRating,
        competitors: (d.competitors ?? [])
            .map((c: any) => ({
                companyName: cleanText(c.companyName) ?? '',
                description: cleanText(c.description),
                // API uses canonical lowercase field names. Earlier mappings
                // looked up `c['location (HQ)']` and `c.Employees` — those
                // never resolved, so location and employees rendered as
                // "Not disclosed" even when the API had real values. Falling
                // back to the older shapes preserves backward compat if the
                // upstream API ever flips back.
                website: cleanText(c.website),
                location: cleanText(c.location ?? c['location (HQ)']),
                revenue: cleanText(c.revenue),
                employees: cleanText(c.employees ?? c.Employees),
                foundingYear: cleanText(c.foundingYear),
                totalFunding: cleanText(c.totalFunding),
                fundingStage: cleanText(c.fundingStage),
                keyAchievements: cleanText(c.keyAchievements),
            }))
            .filter((c: any) => c.companyName),
        news,
    };
}

export function normalizeManagement(raw: any): ManagementProps | undefined {
    const d = raw?.data;
    if (!d) return undefined;
    const seen = new Set<string>();
    const mom = (d.employeeData?.momEmployeeData ?? [])
        .filter((e: any) => {
            if (seen.has(e.yearMonth)) return false;
            seen.add(e.yearMonth);
            return true;
        })
        .map((e: any) => ({ yearMonth: e.yearMonth, count: e.employeeCount }));
    return {
        profiles: (d.managementProfile?.profiles ?? []).map((p: any) => {
            // Best-effort extraction — different API shapes have surfaced these
            // under different keys. Anything not present here will render as "—".
            const presentRaw = p.presentBoards ?? p.currentDirectorships ?? p.otherBoards ?? [];
            const pastRaw = p.pastBoards ?? p.formerDirectorships ?? [];
            const toBoard = (b: any) =>
                typeof b === 'string' ? { name: b } : { name: b?.name ?? b?.companyName ?? '—', since: b?.since, from: b?.from, to: b?.to };
            return {
                name: p.name,
                designation: p.designation ?? 'Director',
                age: p.age,
                din: p.din,
                isActive: p.isActive,
                tenureBeginDate: p.tenureBeginDate,
                legalCasesCount: p.legalCasesCount,
                stake: p.stake ?? p.shareholdingPercent ?? p.holdingPercentage,
                creditBureau: p.creditBureau ?? p.bureauScore ?? p.cibilScore,
                otherBoardsCount:
                    typeof p.otherBoardsCount === 'number'
                        ? p.otherBoardsCount
                        : typeof p.otherDirectorships === 'number'
                        ? p.otherDirectorships
                        : Array.isArray(p.otherBoards)
                        ? p.otherBoards.length
                        : Array.isArray(presentRaw)
                        ? presentRaw.length
                        : undefined,
                gender: p.gender,
                nationality: p.nationality,
                pan: p.pan ?? p.maskedPan,
                bureauScoreLabel: p.bureauScoreLabel ?? p.bureauBand ?? p.cibilLabel,
                legalCasesAgainst: typeof p.legalCasesAgainst === 'number' ? p.legalCasesAgainst : p.legalCasesCountAgainst,
                legalCasesFiledBy: typeof p.legalCasesFiledBy === 'number' ? p.legalCasesFiledBy : p.legalCasesCountFiledBy,
                pepStatus: p.pepStatus ?? p.pep,
                sanctions: p.sanctions ?? p.sanctionStatus,
                adverseMedia: p.adverseMedia ?? p.adverseMediaStatus,
                presentBoards: Array.isArray(presentRaw) ? presentRaw.map(toBoard) : undefined,
                pastBoards: Array.isArray(pastRaw) ? pastRaw.map(toBoard) : undefined,
            };
        }),
        employeeRange: d.employeeData?.currentEmployeeRange,
        momEmployee: mom,
    };
}

const RP_PARTY_PLACEHOLDER = /^(\s*(n\/?a|na|none|nil|null|undefined|—|-+)\s*)$/i;
const cleanPartyField = (v: any): string | undefined => {
    if (v == null) return undefined;
    const s = String(v).trim();
    if (!s || RP_PARTY_PLACEHOLDER.test(s)) return undefined;
    return s;
};

const SEV_SET = new Set(['LOW', 'MEDIUM', 'HIGH', 'SEVERE']);
const cleanRisk = (v: any): Severity | undefined => {
    const s = String(v ?? '').toUpperCase();
    return SEV_SET.has(s) ? (s as Severity) : undefined;
};

const formatPartyDate = (raw: any): string | undefined => {
    if (!raw) return undefined;
    const s = String(raw);
    if (s.length >= 10 && s.includes('T')) return s.slice(0, 10);
    return cleanPartyField(s);
};

// Pick the side of an edge that ISN'T the assessee. The Polus graph stores
// every relationship as a directed edge between two named entities; for
// company-typed parties the assessee usually sits at one fixed end, but
// which end varies by bucket (SHAREHOLDER puts the assessee at toName,
// SUBSIDIARY at fromName, etc.). Picking the non-assessee side gives the
// row a meaningful primary-entity name. Falls back to whichever side has
// a non-empty value if neither matches.
const otherEnd = (fromName: any, toName: any, assesseeNorm: string): string | undefined => {
    const f = cleanPartyField(fromName);
    const t = cleanPartyField(toName);
    const norm = (s?: string) => (s ?? '').toLowerCase().trim();
    if (f && norm(f) !== assesseeNorm) return f;
    if (t && norm(t) !== assesseeNorm) return t;
    return f || t;
};

function partyRowsFromMap(map: Record<string, any[]> | undefined, role: string, assesseeNorm: string): RelatedPartyEntity[] {
    if (!map) return [];
    return Object.values(map).flatMap((rows) => {
        if (!Array.isArray(rows) || rows.length === 0) return [];
        return rows.map((entry: any) => {
            const isPerson = entry.type === 'PERSON';
            // Each entry.rows[] entry is a degree-2 edge from another entity
            // back to this party. For a person we want fromName (the human),
            // for a company we want whichever end isn't the assessee — see
            // `otherEnd`. Dedupe by name so the same board doesn't appear
            // twice when two edges connect to it.
            const rawRows: any[] = Array.isArray(entry.rows) ? entry.rows : [];
            // Dedupe on name + edgeType, not name alone: the same second-degree
            // entity can be reached via two distinct edges (e.g. DIRECTOR on
            // one, DESIGNATED_PARTNER on another) and each now renders its own
            // exact relation label (see resolveNestedRelationLabel in
            // RelatedParties.tsx). A name-only dedupe would silently drop
            // whichever edge lost the race. Missing edgeType still gets its own
            // bucket (empty-string key) rather than colliding with all others.
            const seenBoards = new Set<string>();
            const otherBoards = rawRows
                .map((r: any) => {
                    const name = otherEnd(r.fromName, r.toName, assesseeNorm);
                    if (!name) return null;
                    return {
                        name,
                        designation: cleanPartyField(r.edgeValue),
                        risk: cleanRisk(r.risk),
                        alertsCount: Array.isArray(r.alerts) ? r.alerts.length : 0,
                        kid: cleanPartyField(r.fromKid) || cleanPartyField(r.toKid),
                        edgeType: cleanPartyField(r.edgeType),
                        entityType: r.type,
                    };
                })
                .filter((b): b is NonNullable<typeof b> => {
                    if (!b) return false;
                    const key = `${b.name.toLowerCase()}|${(b.edgeType ?? '').toLowerCase()}`;
                    if (seenBoards.has(key)) return false;
                    seenBoards.add(key);
                    return true;
                });
            // otherEntities stays deduped by NAME ONLY — it feeds Management.tsx's
            // per-director "Other Directorships" card (via presentFromRelated /
            // enrichDirectorProfile), which only cares about the company name.
            // otherBoards can now legitimately hold two entries for the same
            // company (different edgeType each), and mapping it straight to
            // otherEntities would duplicate that board on the Management card.
            const seenNames = new Set<string>();
            const otherEntities = otherBoards
                .map((b) => b.name)
                .filter((name) => {
                    const key = name.toLowerCase();
                    if (seenNames.has(key)) return false;
                    seenNames.add(key);
                    return true;
                });
            const primaryName = isPerson ? cleanPartyField(entry.fromName) : otherEnd(entry.fromName, entry.toName, assesseeNorm);
            return {
                name: primaryName ?? '—',
                role,
                entityType: entry.type,
                degree: String(entry.degree ?? '1'),
                otherEntities,
                otherBoards,
                designation: cleanPartyField(entry.edgeValue),
                risk: cleanRisk(entry.risk),
                alertsCount: Array.isArray(entry.alerts) ? entry.alerts.length : 0,
                kid: cleanPartyField(isPerson ? entry.fromKid : entry.toKid),
                updatedAt: formatPartyDate(entry.updatedAt),
            };
        });
    });
}

// Auto-detect the assessee's name as it appears in the Polus edge data by
// finding the name that's repeated most across all entries' top-level
// fromName/toName fields. The assessee anchors every edge so its name
// dominates the histogram. The passed-in `assesseeName` (e.g. display
// "IndiGo") is added as a hint but the auto-detected form (e.g. legal
// "INTERGLOBE AVIATION LIMITED") usually wins.
const detectAssesseeName = (data: any, hint?: string): string => {
    const counts = new Map<string, number>();
    const bump = (s?: string) => {
        const k = cleanPartyField(s)?.toLowerCase().trim();
        if (!k) return;
        counts.set(k, (counts.get(k) ?? 0) + 1);
    };
    Object.values(data ?? {}).forEach((bucket: any) => {
        if (!bucket || typeof bucket !== 'object') return;
        Object.values(bucket).forEach((rows: any) => {
            if (!Array.isArray(rows)) return;
            rows.forEach((entry: any) => {
                if (entry?.type !== 'PERSON') {
                    bump(entry?.fromName);
                    bump(entry?.toName);
                }
            });
        });
    });
    let best = '';
    let bestN = 0;
    counts.forEach((n, k) => {
        if (n > bestN) {
            best = k;
            bestN = n;
        }
    });
    // Only trust auto-detect when one name strongly dominates (≥30% of edges
    // touched). Otherwise fall back to the passed-in hint.
    const total = Array.from(counts.values()).reduce((s, n) => s + n, 0);
    if (total > 0 && bestN / total >= 0.3) return best;
    return (hint ?? '').toLowerCase().trim();
};

export function normalizeRelatedParties(raw: any, assesseeName?: string): RelatedPartiesProps | undefined {
    const data = raw?.responseBody?.UW_FETCH_RELATED_PARTIES?.data;
    if (!data) return undefined;
    const assesseeNorm = detectAssesseeName(data, assesseeName);
    return {
        directors: partyRowsFromMap(data.DIRECTOR, 'Director', assesseeNorm),
        shareholders: partyRowsFromMap(data.SHAREHOLDER, 'Shareholder', assesseeNorm),
        former: partyRowsFromMap(data.FORMER_DIRECTOR, 'Former Director', assesseeNorm),
        holdings: partyRowsFromMap(data.HOLDING_COMPANY, 'Holding Company', assesseeNorm),
        subsidiaries: partyRowsFromMap(data.SUBSIDIARY, 'Subsidiary', assesseeNorm),
        associates: partyRowsFromMap(data.ASSOCIATE, 'Associate', assesseeNorm),
        jointVentures: partyRowsFromMap(data.JOINT_VENTURE, 'Joint Venture', assesseeNorm),
        sisterConcerns: partyRowsFromMap(data.SISTER_CONCERN, 'Sister Concern', assesseeNorm),
    };
}

export function normalizeShareholding(raw: any): ShareholdingProps | undefined {
    const g = raw?.graphs?.shareholdingPattern;
    if (!g) return undefined;
    const plot = g.plots?.[0];
    const financialEndDate = g.metadata?.financialEndDate;
    if (!plot) return { rows: [], financialEndDate };
    const toPct = (v: any): number => {
        if (typeof v === 'number') return v;
        const n = parseFloat(
            String(v ?? '')
                .replace('%', '')
                .trim(),
        );
        return Number.isFinite(n) ? n : 0;
    };
    let source: any[] = Array.isArray(plot.rowData) ? plot.rowData : [];
    let rows: ShareholdingProps['rows'] = source.map((r: any) => ({
        holderName: r.holderName ?? r.name ?? '—',
        holderType: r.holderType ?? r.type ?? '—',
        percent: toPct(r.percent ?? r.percentage ?? 0),
    }));
    if (!rows.length && Array.isArray(plot.plotValues)) {
        rows = plot.plotValues
            .filter((r: any) => String(r.rowVal ?? '').toLowerCase() !== 'total')
            .map((r: any) => ({
                holderName: r.rowVal ?? '—',
                holderType: r.rowVal2 ?? '—',
                percent: toPct(r.rowVal3),
            }));
    }
    return { rows, financialEndDate };
}

export function normalizeFunding(raw: any): FundingHistoryProps | undefined {
    const d = raw?.responseBody;
    if (!d) return undefined;
    const map = (rows: any[]) =>
        rows.map((r) => ({
            date: r.allotmentDate,
            amount: r.fundingAmount,
            instrument: r.instrument,
            type: r.fundingType,
        }));
    return {
        totalFundingCr: d.totalFundingAmount ?? 0,
        totalDebentureCr: d.totalDebentureAmount ?? 0,
        lastUpdatedAt: d.lastUpdatedDate?.[0]?.lastUpdatedAt,
        fundingRows: map(d.fundingHistoryData ?? []),
        debentureRows: map(d.debentureHistoryData ?? []),
    };
}

const MCA_STATUS_KEYS = /"(statusCode|status_code|request_id|requestId|api_version|apiVersion|metadata)"/;
const MCA_STATUS_MESSAGES = /Request\s+(Received|Submitted)|in\s+progress|Still\s+processing/i;

export function normalizeMca(raw: any): McaDocsProps | undefined {
    const mca = raw?.responseBody?.mcaDocApiData;
    if (!mca) return undefined;
    const resData = mca.resData;
    let summary: string | undefined;
    if (resData != null) {
        const text = typeof resData === 'string' ? resData : JSON.stringify(resData);
        const trimmed = text.trim();
        const looksLikeStatusBlob = trimmed.startsWith('{') && (MCA_STATUS_KEYS.test(trimmed) || MCA_STATUS_MESSAGES.test(trimmed));
        const looksLikePlainText = typeof resData === 'string' && !trimmed.startsWith('{') && !trimmed.startsWith('[');
        if (!looksLikeStatusBlob && looksLikePlainText) summary = trimmed;
    }
    return {
        isFetching: !!mca.isFetching,
        updatedAt: mca.updatedAt,
        summary,
    };
}

function monthCellFromPlot(xValue: string, yValue: number, yValue2?: number, yValue3?: number): MonthCell {
    const [mon, year] = xValue.split('_');
    const yearShort = (year ?? '').slice(-2);
    let status: MonthCell['status'];
    if (yValue === 3 || (yValue3 ?? 0) > 0) status = 'default';
    else if ((yValue2 ?? 0) > 0) status = 'delayed';
    else if (yValue === 1 || yValue === 2) status = 'on_time';
    else status = 'missing';
    return {
        label: xValue,
        short: MONTH_SHORT[mon] ?? mon,
        yearShort,
        status,
        delayDays: (yValue2 ?? 0) > 0 ? yValue2 : undefined,
    };
}

export function normalizeCompliances(gstr1: any, gstr3b: any, epf: any, headline: any): CompliancesProps | undefined {
    if (!gstr1 && !gstr3b && !epf && !headline) return undefined;
    const readPlot = (raw: any, key: string) => {
        const plot = raw?.graphs?.[key]?.plots?.[0]?.plotValues ?? [];
        return plot.map((p: any) => monthCellFromPlot(p.xValue, p.yValue, p.yValue2, p.yValue3));
    };
    const cardsSrc = headline?.tabData?.headlinePanelData;
    const cards =
        cardsSrc?.cards?.map((k: string) => {
            const card = cardsSrc.data?.[k] ?? {};
            return { key: k, title: card.title ?? k, value: card.value, duration: card.metricDuration };
        }) ?? [];
    return {
        gstr1: readPlot(gstr1, 'gstr1FilingStatus'),
        gstr3b: readPlot(gstr3b, 'gstr3bFilingStatus'),
        epf: readPlot(epf, 'epfCompliance'),
        gstr1Pct: gstr1?.graphs?.gstr1FilingStatus?.metadata?.primaryText,
        gstr3bPct: gstr3b?.graphs?.gstr3bFilingStatus?.metadata?.primaryText,
        epfPct: epf?.graphs?.epfCompliance?.metadata?.primaryText,
        headlineCards: cards,
    };
}

const toIsoDateOnly = (value: unknown): string | undefined => {
    if (value == null) return undefined;
    const raw = typeof value === 'string' ? value.trim() : String(value);
    if (!raw) return undefined;
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString().slice(0, 10);
};

function toLegalCase(c: any): LegalCase {
    const filingDate = toIsoDateOnly(c.filingDate);
    const decisionDate = toIsoDateOnly(c.decisionDate);
    return {
        caseNum: c.caseNum ?? '—',
        filedBy: c.filedBy ?? '—',
        filedAgainst: c.filedAgainst ?? '—',
        category: c.caseCategory ?? '—',
        court: c.court ?? '—',
        state: c.state ?? '—',
        district: c.district ?? undefined,
        status: c.status ?? '—',
        filingDate,
        nextHearingDate: c.nextHearingDate ?? undefined,
        decisionDate,
    };
}

// Figma 32:102215 — the Regulatory Checks matrix shows these 8 dimensions
// per entity, ordered top-to-bottom. The API key on the right maps to
// `riskProfile[*]` from Polus AML/PEP. Some keys have spaces.
const AML_PEP_DIMENSIONS: Array<{ label: string; key: string }> = [
    { label: 'Political Exposed Person', key: 'PepCheck' },
    { label: 'Global Sanctions', key: 'SanctionCheck' },
    { label: 'AML Penalties', key: 'AmlCheck' },
    { label: 'Adverse Press Coverage', key: 'ApcCheck' },
    { label: 'Indian Watchlists', key: 'Indian Watchlists' },
    { label: 'Global Watchlists', key: 'International Watchlist' },
    { label: 'State Owned Enterprises', key: 'State Owned Enterprises' },
    { label: "Other Countries' Watchlists", key: 'otherWatchlist' },
];

const scoreToStatus = (raw: unknown): { score?: number; status: 'clear' | 'watch' | 'hit' | 'unavailable' } => {
    if (raw == null || raw === '') return { status: 'unavailable' };
    const n = Number(raw);
    if (!Number.isFinite(n)) return { status: 'unavailable' };
    if (n <= 0) return { score: n, status: 'clear' };
    if (n < 80) return { score: n, status: 'watch' };
    return { score: n, status: 'hit' };
};

export function normalizeLegalRisk(legal: any, amlPep: any): LegalRiskProps | undefined {
    const r = legal?.legalCasesResponse;
    if (!r) return undefined;
    const mapGroup = (g: any): LegalGroup => ({
        name: g.name,
        cases: (g.cases ?? []).map(toLegalCase),
    });
    const amlData: any[] = amlPep?.responseBody?.UW_FETCH_AML_PEP_DATA?.data ?? [];
    const amlPepHits = amlData
        .map((entry) => {
            const profile = entry?.riskProfile ?? {};
            const flagged: string[] = [];
            let maxScore = 0;
            Object.entries(profile).forEach(([bucket, raw]) => {
                const score = Number(raw);
                if (Number.isFinite(score) && score > 0) {
                    flagged.push(bucket);
                    if (score > maxScore) maxScore = score;
                }
            });
            return {
                name: String(entry?.name ?? '—'),
                type: (entry?.type === 'organization' ? 'organization' : 'individual') as 'organization' | 'individual',
                docId: entry?.docId ?? undefined,
                flagged,
                maxScore,
            };
        })
        .filter((h) => h.flagged.length > 0);
    const amlPepEntities = amlData.map((entry) => ({
        // Don't truncate — users couldn't tell which entity each card belonged
        // to when names like "KLAUS IT SOLUTIONS PRIVATE LIMITED" got chopped
        // to "KLAUS IT SOLUTIONS PRIVATE…". The card title row already reserves
        // 2 lines (PdfRegulatoryCard `titleMaxLines: 2`), so longer names just
        // wrap. If a name exceeds 2 lines it'll be visually cropped by the
        // card minHeight — that's preferable to making short names ambiguous.
        name: String(entry?.name ?? '—'),
        type: (entry?.type === 'organization' ? 'organization' : 'individual') as 'organization' | 'individual',
        docId: entry?.docId ?? undefined,
        dimensions: AML_PEP_DIMENSIONS.map(({ label, key }) => {
            const { score, status } = scoreToStatus(entry?.riskProfile?.[key]);
            return { label, score, status };
        }),
    }));
    // Dedupe — the AML/PEP API can return the same entity multiple times
    // (e.g. when the assessee is matched against multiple watchlists or when
    // upstream sources duplicate organization records, each with a different
    // docId but identical risk fingerprint). Key on
    // `name|type|<dimensions-status-fingerprint>` so cards that would render
    // visually identical collapse to one. Two truly different entities with
    // the same name will differ in at least one dimension status and survive.
    const seenKeys = new Set<string>();
    const dedupedEntities = amlPepEntities.filter((e) => {
        const fingerprint = e.dimensions.map((d) => d.status).join(',');
        const key = `${e.name.toLowerCase()}|${e.type}|${fingerprint}`;
        if (seenKeys.has(key)) return false;
        seenKeys.add(key);
        return true;
    });
    return {
        casesAgainst: (r.legalCasesFiledAgainstCompanyOrDirector ?? []).map(mapGroup),
        casesBy: (r.legalCasesFiledByCompanyOrDirector ?? []).map(mapGroup),
        lastFetchedDate: r.lastFetchedDate,
        amlPepCount: amlData.length,
        amlPepHits: amlPepHits.length ? amlPepHits : undefined,
        amlPepEntities: dedupedEntities.length ? dedupedEntities : undefined,
    };
}

const SEVERITIES: Severity[] = ['SEVERE', 'HIGH', 'MEDIUM', 'LOW'];
function sev(input: string): Severity {
    const up = (input || '').toUpperCase();
    return (SEVERITIES.includes(up as Severity) ? up : 'MEDIUM') as Severity;
}

function severityFromTrend(trend: string, label: string): Severity {
    const t = (trend || '').toLowerCase();
    const l = (label || '').toLowerCase();
    const negativeLabel = /churn|default|delay|delinquen|risk|concentration/.test(l);
    if (t === 'down' && negativeLabel) return 'SEVERE';
    if (t === 'down') return 'HIGH';
    if (t === 'up' && negativeLabel) return 'HIGH';
    if (t === 'up') return 'LOW';
    return 'MEDIUM';
}

export function normalizeAlerts(raw: any): AlertsProps | undefined {
    if (!raw) return undefined;
    const listFromBody = raw?.responseBody?.alerts;
    const flatList = raw?.alerts;
    const source: any[] = Array.isArray(listFromBody) ? listFromBody : Array.isArray(flatList) ? flatList : [];
    if (source.length === 0) return undefined;
    const alerts = source.map((a: any) => {
        const severity: Severity = a.severity ? sev(a.severity) : severityFromTrend(a.trend, a.label);
        return {
            severity,
            description: a.description ?? a.text ?? '—',
            category: a.category ?? a.label ?? 'General',
            alertDate: a.alertDate ?? a.date ?? '',
            entityName: a.entityName || undefined,
            entityType: a.entityType || undefined,
            source: a.source || undefined,
        };
    });
    const totalRecords = raw?.responseBody?.totalRecords ?? raw?.totalRecords ?? alerts.length;
    return { alerts, totalRecords };
}

export function buildReportData(sources: {
    assesseeBasic: any;
    // Shape: { data: { cinNumber, panNumber, managementProfile, employeeData, headquarterLocation, ... } }
    // Live: getCompanyOverviewData → /profile/assessee/overview/main
    // Fixture: profileOverview.json
    mainOverview: any;
    score: any;
    // Shape: { responseBody: { data: { overview, sectorDetails, sectorOutlook, newsReport, competitors, funding, ... } } }
    // Live: getCompanyOverviewDataV2 → /unifier/getCompanyOverview
    // Fixture: companyOverview.json
    unifiedOverview: any;
    relatedParties: any;
    shareholding: any;
    fundingHistory: any;
    mcaDocs: any;
    gstr1: any;
    gstr3b: any;
    epf: any;
    compliancesHeadline: any;
    legalCases: any;
    amlPep: any;
    alerts: any;
    // Shape: { orgInfo: { orgName, orgDisplayName, cinNumber, identifier, accountType, customFields } }
    // Live: fetchOrgInfoService → /account/fetch/orgInfo
    orgInfo?: any;
    insightsOverview?: any;
    insightsCompliance?: any;
    insightsLegalChecks?: any;
    insightsAuditedFinancials?: any;
    insightsRelatedParty?: any;
    auditedFinancials?: any;
    auditedStatements?: any;
    provisionalStatements?: any;
    bankingOverview?: any;
    mcaCharges?: any;
    directorsDeep?: any[];
    gstOverview?: any;
    revenueCharts?: any;
    gstr3bRevenue?: any;
    b2bRevenue?: any;
    b2bExpense?: any;
    expenseCharts?: any;
}): ReportData {
    const identity = normalizeIdentity(sources.assesseeBasic, sources.mainOverview, sources.unifiedOverview, sources.orgInfo);
    return {
        identity,
        score: normalizeScore(sources.score),
        overview: normalizeOverview(sources.unifiedOverview),
        management: normalizeManagement(sources.mainOverview),
        // Pass the assessee's orgName so the related-parties normalizer can
        // pick the non-assessee end of each company edge (otherwise every
        // primary entity in Shareholders / Subsidiaries / Sister Concerns
        // surfaces as the assessee's own legal name).
        relatedParties: normalizeRelatedParties(sources.relatedParties, identity?.orgName),
        shareholding: normalizeShareholding(sources.shareholding),
        funding: normalizeFunding(sources.fundingHistory),
        mca: normalizeMca(sources.mcaDocs),
        compliances: normalizeCompliances(sources.gstr1, sources.gstr3b, sources.epf, sources.compliancesHeadline),
        legalRisk: normalizeLegalRisk(sources.legalCases, sources.amlPep),
        alerts: normalizeAlerts(sources.alerts),
        insights: normalizeInsights({
            overview: sources.insightsOverview,
            compliance: sources.insightsCompliance,
            legalChecks: sources.insightsLegalChecks,
            auditedFinancials: sources.insightsAuditedFinancials,
            relatedParty: sources.insightsRelatedParty,
        }),
        auditedStatements: normalizeAuditedStatements(sources.auditedStatements),
        provisionalStatements: normalizeProvisionalStatements(sources.provisionalStatements),
        bankingOverview: normalizeBankingOverview(sources.bankingOverview),
        mcaCharges: normalizeMcaCharges(sources.mcaCharges),
        directorsDeep: normalizeDirectorsDeep(sources.directorsDeep),
        directorBureau: normalizeDirectorBureau(sources.directorsDeep),
        financialMetrics: normalizeFinancialMetrics(sources.auditedFinancials),
        gstOverview: normalizeGstOverview(sources.gstOverview),
        gstRevenue: normalizeGstRevenue(sources.gstr3bRevenue, sources.b2bRevenue, sources.gstOverview, sources.revenueCharts),
        gstExpenses: normalizeGstExpenses(sources.gstr3bRevenue, sources.b2bExpense, sources.expenseCharts),
        aggBanking: normalizeAggBanking(sources.bankingOverview),
    };
}
